// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { useCallback, useEffect } from 'react';
import Pusher, { PresenceChannel } from 'pusher-js';

import {
  setChannel,
  setMessage,
  setMessageSeverity,
  setLiveUsers,
  setLiveViewer,
  State,
  Action,
  setPusher,
  setMessageChannel,
} from 'presentations/reducers/MeetingWidget';
import { SeverityType } from 'presentations/views/components/atoms/Snackbar';
import { LiveUser } from 'presentations/api/schema/user';
import MeetingCall from 'domains/models/meeting_call';

interface Props {
  state: State;
  dispatch: (action: Action) => void;
}

export interface UserMouseEvent {
  userId: string;
  x: number;
  y: number;
}

interface UserUpdateEvent {
  userId: string;
  update: {
    callUserId?: string | null;
    pageId?: string | null;
    display?: string;
    x?: number;
    y?: number;
    shareScreenUser?: LiveUser | null;
  };
}

interface RequestInitDataEvent {
  from: string;
}

interface RespondInitDataEvent {
  to: string;
  data: LiveUser;
  position: MoveUser;
}

interface MoveUser {
  x: number;
  y: number;
}

let liveViewer: LiveUser | null = null;
let liveUsers: any = {};
let moveUsers: any = {};
let mouseX = 0;
let mouseY = 0;
let sendMouseEvent: UserMouseEvent | null;

export function formatClientEventName(teamUid: string, name: string) {
  return `client-${teamUid}-${name}`;
}

function dispatchMoveUsers(moveUsers: any) {
  const event = new CustomEvent('MoveUsers', { detail: moveUsers });
  document.dispatchEvent(event);
}

export function usePusher(props: Props) {
  const t = translateInComponent(translations);
  const { state, dispatch } = props;

  function showMessage(message: string) {
    dispatch(setMessage(message));
    dispatch(setMessageSeverity(SeverityType.Info));
  }

  function clientEventName(name: string) {
    if (state.viewer) {
      return formatClientEventName(state.viewer.team!.uid, name);
    } else {
      return null;
    }
  }
  function clientEventBind(
    channel: PresenceChannel,
    name: string,
    callback: any,
  ) {
    const eventName = clientEventName(name);
    if (eventName) {
      channel.bind(eventName, callback);
    }
  }
  function clientEventTrigger(
    channel: PresenceChannel,
    name: string,
    data: any,
  ) {
    const eventName = clientEventName(name);
    if (eventName) {
      channel.trigger(eventName, data);
    }
  }

  const connect = useCallback(() => {
    if (!state.viewer) return;
    if (!state.uid) return;

    let pusher = state.pusher;
    if (!pusher) {
      pusher = new Pusher(String(process.env['PUSHER_KEY']), {
        cluster: process.env['PUSHER_CLUSTER'],
        // must relative path
        authEndpoint: '/api/v1/webhooks/pusher/auth',
      });
      dispatch(setPusher(pusher));
    }
    const channel = pusher.subscribe(
      MeetingCall.createChannelName(state.viewer.team!.uid, state.uid),
    ) as PresenceChannel;
    bindEvent(channel, dispatch, dispatchMoveUsers);
  }, [state.viewer, state.uid, dispatch, dispatchMoveUsers]);

  function disconnect() {
    state.channel?.unsubscribe();
    dispatch(setChannel(null));
    liveViewer = null;
    dispatch(setLiveViewer(liveViewer));
    liveUsers = {};
    dispatch(setLiveUsers(liveUsers));
    moveUsers = {};
    dispatchMoveUsers(moveUsers);
  }

  async function bindEvent(
    channel: PresenceChannel,
    dispatch: any,
    setMoveUsers: Function,
  ) {
    try {
      channel.bind('pusher:subscription_succeeded', (members: any) => {
        const me = members.me;
        liveViewer = me.info;
        if (!liveViewer) return;

        dispatch(setLiveViewer(liveViewer));

        members.each((member: any) => {
          if (!liveUsers[member.id]) {
            liveUsers[member.id] = member.info;
            moveUsers[member.id] = { x: 0, y: 0 };
          }
        });
        dispatch(setLiveUsers({ ...liveUsers }));
        setMoveUsers(moveUsers);

        const event: RequestInitDataEvent = {
          from: liveViewer.uid,
        };
        dispatch(setChannel(channel));
        clientEventTrigger(channel, 'request-init-data', event);
      });

      clientEventBind(channel, 'request-init-data', function (
        initEvent: RequestInitDataEvent,
      ) {
        if (!liveViewer) return;
        const position = moveUsers[liveViewer.uid];
        const event: RespondInitDataEvent = {
          to: initEvent.from,
          data: liveViewer,
          position,
        };
        clientEventTrigger(channel, 'send-init-data', event);
      });

      clientEventBind(channel, 'send-init-data', function (
        event: RespondInitDataEvent,
      ) {
        if (event.to === liveViewer?.uid) {
          const liveUser = event.data;
          liveUsers[liveUser.uid] = liveUser;
          dispatch(setLiveUsers({ ...liveUsers }));

          moveUsers[liveUser.uid] = event.position;
          setMoveUsers(moveUsers);
        }
      });

      channel.bind('pusher:subscription_error', (data: any) => {
        console.log(data.error);
      });

      channel.bind('pusher:member_added', function (member: any) {
        const newUser = member.info;
        liveUsers[member.id] = newUser;
        dispatch(setLiveUsers({ ...liveUsers }));
        moveUsers[member.id] = { x: 0, y: 0 };
        setMoveUsers(moveUsers);
      });

      channel.bind('pusher:member_removed', function (member: any) {
        delete liveUsers[member.id];
        dispatch(setLiveUsers({ ...liveUsers }));
        delete moveUsers[member.id];
        setMoveUsers(moveUsers);
      });

      clientEventBind(channel, 'moved', function (event: UserMouseEvent) {
        moveUsers[event.userId] = { x: event.x - 28, y: event.y - 2 };
        setMoveUsers({ ...moveUsers });
      });

      clientEventBind(channel, 'update', function (event: UserUpdateEvent) {
        if (!liveUsers[event.userId]) return;
        if (event.update.callUserId !== undefined) {
          const updateUser = liveUsers[event.userId];
          if (!updateUser.callUserId) {
            showMessage(t('User Connected', { name: updateUser.name }));
          }
          updateUser.callUserId = event.update.callUserId;
          liveUsers[event.userId] = updateUser;

          moveUsers[event.userId] = { x: mouseX, y: mouseY };
          setMoveUsers({ ...moveUsers });
        }
        if (event.update.display !== undefined) {
          liveUsers[event.userId].display = event.update.display;
        }
        if (event.update.pageId !== undefined) {
          liveUsers[event.userId].pageId = event.update.pageId;
        }
        dispatch(setLiveUsers({ ...liveUsers }));
      });
      return channel;
    } catch (error) {
      console.error('Error writing document: ', error);
    }
  }

  function setLocalCallUserId(callUserId: string | null) {
    const channel = state.channel;
    if (!channel) return;
    if (!liveViewer) return;

    liveViewer.callUserId = callUserId;
    liveViewer.display = '';
    dispatch(setLiveViewer({ ...liveViewer }));

    liveUsers[liveViewer.uid] = liveViewer;
    moveUsers[liveViewer.uid] = { x: mouseX, y: mouseY };
    dispatch(setLiveUsers({ ...liveUsers }));
    const event: UserUpdateEvent = {
      userId: liveViewer.uid,
      update: {
        callUserId,
        display: liveViewer.display,
        x: mouseX,
        y: mouseY,
      },
    };
    clientEventTrigger(channel, 'update', event);
  }

  const onMouseMove = useCallback((e: any) => {
    mouseX = e.pageX || e.clientX;
    mouseY = e.pageY || e.clientY;
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', onMouseMove);
    return () => window.removeEventListener('mousemove', onMouseMove);
  }, [onMouseMove]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (sendMouseEvent) {
        const channel = state.channel;
        if (!channel) return;
        clientEventTrigger(channel, 'moved', sendMouseEvent);
        sendMouseEvent = null;
      }
    }, 600);
    return function cleanUp() {
      clearInterval(timer);
    };
  }, [state.channel]);

  async function setCurrentPage(pageId: string | null) {
    const channel = state.channel;
    if (!channel) return;
    if (!liveViewer) return;
    liveViewer.pageId = pageId;
    dispatch(setLiveViewer({ ...liveViewer }));

    liveUsers[liveViewer.uid].pageId = pageId;
    dispatch(setLiveUsers(liveUsers));

    const event: UserUpdateEvent = {
      userId: liveViewer.uid,
      update: {
        pageId,
      },
    };
    clientEventTrigger(channel, 'update', event);
  }

  return {
    state: {},
    actions: {
      setCurrentPage,
      setLocalCallUserId,
      connect,
      disconnect,
    },
  };
}
